// Copyright (c) Meta Platforms, Inc. and affiliates.
// All rights reserved.

// This source code is licensed under the license found in the
// LICENSE file in the root directory of this source tree.

import { InferenceSession, Tensor } from "onnxruntime-web";
import React, { useContext, useEffect, useRef, useState } from "react";
import "./assets/scss/App.scss";
import { handleImageScale } from "./components/helpers/scaleHelper";
import { modelScaleProps } from "./components/helpers/Interfaces";
import { onnxMaskToImage } from "./components/helpers/maskUtils";
import { modelData } from "./components/helpers/onnxModelAPI";
import Stage from "./components/Stage";
import AppContext from "./components/hooks/createContext";
const ort = require("onnxruntime-web");
/* @ts-ignore */
import npyjs from "npyjs";
import { Url } from "url";


// Define image, embedding and model paths

// const data = {"IMAGE_PATH": "../assets/data/comic.png",
// "IMAGE_EMBEDDING": "../assets/data/comic.npy",
// "MODEL_DIR": "../assets/data/sam_onnx_quantized_example.onnx",
// }
interface IPerson {
  IMAGE_PATH: string;
  IMAGE_EMBEDDING: string;
  MODEL_DIR: string;
  id:string;
  defaultpoint: any;
  // intervalthingmoji: any;
}

function Segmenter({IMAGE_PATH,IMAGE_EMBEDDING,MODEL_DIR,id,defaultpoint}: IPerson) {
  const {
    // clicks: [clicks],
    selected: [selected], image: [, setImage], maskImg: [, setMaskImg], segmentedImage: [segmentedImage], isBrushing: [isBrushing]
  } = useContext(AppContext)!;
  const [model, setModel] = useState<InferenceSession | null>(null); // ONNX model
  const [tensor, setTensor] = useState<Tensor | null>(null); // Image embedding tensor



  // The ONNX model expects the input to be rescaled to 1024. 
  // The modelScale state variable keeps track of the scale values.
  const [modelScale, setModelScale] = useState<modelScaleProps | null>(null);
  // const selfRef = useRef<HTMLDivElement>(null);
  // Initialize the ONNX model. load the image, and load the SAM
  // pre-computed image embedding
  
  useEffect(() => {
    // Initialize the ONNX model
    const initModel = async () => {
      try {
        if (MODEL_DIR === undefined) return;
        const URL: string = MODEL_DIR;
        // console.log(URL);
        const model = await InferenceSession.create(URL);
        setModel(model);
      } catch (e) {
        console.log(e);
      }
    };
    const start = Date.now();
    initModel();
    const end = Date.now();
    let time_taken = (end - start) / 1000;
    console.log(`Model loaded in ${time_taken} s`)
  }, []);

  useEffect(() => {
    // Load the image
    // const url = new URL(IMAGE_PATH, location.origin);
    loadImage(IMAGE_PATH);
    console.log("image loaded")
  }, [IMAGE_PATH]);

  // useEffect(() => {
  //   if (IMAGE_EMBEDDING!==null){
  //     console.log(IMAGE_EMBEDDING)
  //     console.log("embedding loaded")
  //   // Load the Segment Anything pre-computed embedding
  //   Promise.resolve(loadNpyTensor_new(IMAGE_EMBEDDING, "float32")).then(
  //     (embedding) => setTensor(embedding)
  //   )
  //   // console.log(tensor)
  //   }else{
  //     setTensor(null)
  //   }
  // }, [IMAGE_EMBEDDING]);

  const loadImage = async (url: string) => {
    try {
      const img = new Image();
      img.src = url;
      img.id = "seg-image"
      img.onload = () => {
        const { height, width, samScale } = handleImageScale(img);
        setModelScale({
          height: height,
          width: width,
          samScale: samScale, // scaling factor for image which has been resized to longest side 1024
        });
        img.width = width;
        img.height = height;
        setImage(img);
      };
    } catch (error) {
      console.log(error);
    }
  };

  // Decode a Numpy file into a tensor. 
  const loadNpyTensor = async (tensorFile: string, dType: string) => {
    let npLoader = new npyjs();
    const npArray = await npLoader.load(tensorFile);
    console.log(npArray.shape)
    console.log(npArray.data)
    const tensor = new ort.Tensor(dType, npArray.data, npArray.shape);
    return tensor;
  };

  const loadNpyTensor_new = async (tensorFile: string, dType: string) => {
    const shape = [1, 256, 64, 64];
    const tensor = new ort.Tensor(dType, tensorFile, shape);
    return tensor;
  };


  // Run the ONNX model every time clicks has changed
  // useEffect(() => {
  //   runONNX(clicks);
  // }, [clicks]);
  useEffect(() => {
    const start = Date.now();
    runONNX(selected);
    const end = Date.now();
    let time_taken = (end - start) / 1000;
    console.log(`Model inference in ${time_taken} s`)
  }, [selected]);

  useEffect(() => {
    if (IMAGE_EMBEDDING!==null){
    Promise.resolve(loadNpyTensor_new(IMAGE_EMBEDDING, "float32")).then(
      (embedding) => {setTensor(embedding)
        return embedding
      }
    )
    .then( (embedding) => { 
      // console.log(tensor)
      const start = Date.now();
      runONNXForPoint(defaultpoint,embedding);
      const end = Date.now();
      let time_taken = (end - start) / 1000;
      console.log(`Model inference in ${time_taken} s`)
      console.log("has embedding")
    });
  }else{
    console.log("waiting for embedding back from server to load...")
    setTensor(null)
  }
    // const start = Date.now();
    // console.log(defaultpoint)
    // // if (defaultpoint !== null) {  
    //   runONNX(defaultpoint);
    //   console.log("defaultpoint loaded")
    // // }

    // // runONNX(selected);
    // const end = Date.now();
    // let time_taken = (end - start) / 1000;
    // console.log(`Model inference in ${time_taken} s`)
  }, [defaultpoint,IMAGE_EMBEDDING]);

  const runONNX = async (points: any) => {
    console.log(points)
    try {
      if (model === null ||
        points === null ||
        // selected === null ||
        tensor === null ||
        modelScale === null){
        console.log(model,points,tensor,modelScale)
        return;}
      else {
        // Preapre the model input in the correct format for SAM. 
        // The modelData function is from onnxModelAPI.tsx.
        // console.log(points);
        const feeds = modelData({
          points: points,
          tensor,
          modelScale,
        });
        if (feeds === undefined) return;
        // Run the SAM ONNX model with the feeds returned from modelData()
        const results = await model.run(feeds);
        const output = results[model.outputNames[0]];
        // The predicted mask returned from the ONNX model is an array which is 
        // rendered as an HTML image using onnxMaskToImage() from maskUtils.tsx.
        setMaskImg(onnxMaskToImage(output.data, output.dims[2], output.dims[3]));
      }
    } catch (e) {
      console.log(e);
    }
  };


  const runONNXForPoint = async (points: any,tensor: Tensor) => {
    // console.log(points)
    try {
      if (model === null ||
        points === null ||
        // selected === null ||
        tensor === null ||
        modelScale === null){
        console.log(model,points,tensor,modelScale)
        return;}
      else {
        // Preapre the model input in the correct format for SAM. 
        // The modelData function is from onnxModelAPI.tsx.
        console.log(points);
        const feeds = modelData({
          points: points,
          tensor,
          modelScale,
        });
        if (feeds === undefined) return;
        // Run the SAM ONNX model with the feeds returned from modelData()
        const results = await model.run(feeds);
        const output = results[model.outputNames[0]];
        // The predicted mask returned from the ONNX model is an array which is 
        // rendered as an HTML image using onnxMaskToImage() from maskUtils.tsx.
        setMaskImg(onnxMaskToImage(output.data, output.dims[2], output.dims[3]));
      }
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <div className="h-100 w-100" 
    style={{top:"0px",position:"absolute"}} 
    id={id} >
      <Stage/>
      {/* <div> */}
            {segmentedImage && (
              <img
              src={segmentedImage.src}
              className="center">
              </img>
            )}
            {/* </div> */}
    </div>
  );
}

export default Segmenter;